/**
 * Модальное окно и мелкие формы поверх него.
 *
 * ДИЗАЙН ЗДЕСЬ НАМЕРЕННО ЧЕРНОВОЙ (решение Антона 2026-09-09 19:07 «дизайн потом
 * поправим»): белый прямоугольник по центру, затемнение, две кнопки. Ни анимаций,
 * ни теней с blur, ни ховер-эффектов — они бы всё равно поехали при первой же
 * правке макета. Цвета — только из theme.ts.
 *
 * Что тут НЕ черновое и трогать при полировке нельзя:
 *  - Esc закрывает, клик по затемнению закрывает — иначе окно превращается
 *    в ловушку, если запрос к серверу подвис;
 *  - при открытии фокус уходит в первое поле, чтобы можно было печатать сразу;
 *  - кнопка подтверждения блокируется на время запроса, иначе двойной клик
 *    создаст две комнаты (на сервере с rate limit — ещё и с 429 на второй).
 */
import { useEffect, useRef, useState } from "react";

import { s } from "./styles";
import { color, font, radius, space } from "./theme";

export function Dialog({
  title,
  onClose,
  children,
  footer,
  error,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
  error?: string | null;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Фокус в первое поле формы: диалог, в который надо ещё кликнуть мышью,
    // прежде чем печатать, раздражает больше, чем отсутствие диалога.
    //
    // ИСКЛЮЧАЕМ скрытые file-input'ы и всё с tabindex=-1 (баг 2026-09-24):
    // в настройках комнаты ПЕРВЫЙ input в DOM — это input[type=file] выбора
    // аватара. Фокус уходил в него, а не в осмысленное поле; печатать было
    // некуда. Раньше он был display:none и querySelector всё равно его
    // находил — то есть фокус уходил в никуда.
    const first = boxRef.current?.querySelector<HTMLElement>(
      'input:not([type="file"]):not([type="hidden"]):not([tabindex="-1"]), textarea, select',
    );
    first?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      style={s.dialogBackdrop}
      /* alg-dialog-backdrop/box: на ≤640px мобильный CSS превращает
         центрированное окно 380px в лист на весь экран снизу (спека
         2026-09-20, этап 5 — на 390px окно с полями клаустрофобно). */
      className="alg-dialog-backdrop"
      data-testid="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={s.dialogBox} ref={boxRef} role="dialog" aria-label={title} data-testid="dialog" className="alg-dialog-box">
        <div style={s.dialogTitle} data-testid="dialog-title">
          {title}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: space.md }}>{children}</div>
        {error && (
          <div style={s.dialogError} data-testid="dialog-error" role="alert">
            {error}
          </div>
        )}
        <div style={s.dialogFooter}>{footer}</div>
      </div>
    </div>
  );
}

/** Кнопка диалога. `kind` — только смысл, не оттенок: primary / plain / danger. */
export function DialogButton({
  kind = "plain",
  disabled,
  onClick,
  children,
  testId,
}: {
  kind?: "primary" | "plain" | "danger";
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
}) {
  const base = {
    font: "inherit",
    fontSize: font.body,
    padding: "8px 14px",
    borderRadius: radius.md,
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.5 : 1,
  } as const;
  const skin =
    kind === "primary"
      ? { background: color.accent, color: color.textOnAccent, border: "none" }
      : kind === "danger"
        ? { background: color.danger, color: color.textOnAccent, border: "none" }
        : { background: color.bg, color: color.text, border: `1px solid ${color.border}` };

  return (
    <button
      type="button"
      style={{ ...base, ...skin }}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/** Поле ввода диалога с подписью. */
export function Field({
  label,
  value,
  onChange,
  placeholder,
  testId,
  onEnter,
  problem,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testId?: string;
  onEnter?: () => void;
  /** Причина, по которой введённое нельзя отправить. Показывается под полем
      СРАЗУ, до нажатия кнопки — ошибку надо видеть до отправки. */
  problem?: string | null;
}) {
  return (
    <label style={{ display: "block", fontSize: font.small, color: color.textDim }}>
      {label}
      <input
        style={s.dialogInput}
        value={value}
        placeholder={placeholder}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
      />
      {problem && (
        <div
          style={{ marginTop: 4, color: color.danger, fontSize: font.tiny }}
          data-testid="field-problem"
        >
          {problem}
        </div>
      )}
    </label>
  );
}

/**
 * Обёртка «форма с асинхронным подтверждением».
 *
 * Вынесена, потому что у всех пяти форм (создать комнату, создать DM,
 * пригласить, выйти, отклонить) одна и та же скучная механика: блокировка на
 * время запроса, показ ошибки, закрытие при успехе. Копировать её пять раз
 * означало бы пять мест, где можно забыть снять busy на исключении.
 */
export function useAsyncAction(onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      // Ошибку показываем в диалоге и окно НЕ закрываем: закрытое окно с
      // молча непройденной операцией — это худшее, что тут можно сделать.
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, run, setError };
}
